import { useEffect, useState } from "react";
import { Routes, Route, NavLink, useNavigate, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { api, API, getToken, setToken, BACKEND } from "./api";
import LoginScreen from "./LoginScreen";
import StaffWorkspace from "./staff/StaffWorkspace";
import CustomerPortal from "./customer/CustomerPortal";
import BrokerPortal from "./broker/BrokerPortal";
import { usePageMeta } from "../hooks/usePageMeta";

export default function AgencyApp() {
  usePageMeta("Elin's Agency Platform", "Agency, customer and broker workspace for the Elin's property + finance platform.");
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("checking");
  const navigate = useNavigate();
  const location = useLocation();

  const load = () =>
    api
      .get(`${API}/auth/me`)
      .then(({ data }) => {
        setUser(data);
        setStatus("ready");
      })
      .catch(() => {
        setToken(null);
        setUser(null);
        setStatus("login");
      });

  useEffect(() => {
    if (!getToken()) {
      setStatus("login");
      return;
    }
    load();
  }, []);

  // Route the user to the correct workspace based on role, honouring their current URL
  // prefix if it already matches their role.
  useEffect(() => {
    if (status !== "ready" || !user) return;
    const path = location.pathname;
    const roleHome =
      user.role === "customer" ? "/portal" :
      user.role === "broker" ? "/broker" :
      "/agency";
    const inOtherWorkspace =
      (!path.startsWith(roleHome)) &&
      (path.startsWith("/agency") || path.startsWith("/portal") || path.startsWith("/broker"));
    if (inOtherWorkspace || path === "/") navigate(roleHome, { replace: true });
    // eslint-disable-next-line
  }, [status, user]);

  const logout = () => {
    setToken(null);
    setUser(null);
    setStatus("login");
    navigate("/agency", { replace: true });
  };

  if (status === "checking") {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500 text-sm">Loading…</div>;
  }
  if (status === "login") {
    return <LoginScreen onLoggedIn={(u, token) => { setToken(token); setUser(u); setStatus("ready"); }} />;
  }

  return (
    <>
      <Toaster richColors position="top-right" />
      <Routes>
        <Route path="/*" element={
          window.location.pathname.startsWith("/portal") ? (
            user.role === "customer" ? <CustomerPortal me={user} onLogout={logout} /> :
            <Navigate to={user.role === "broker" ? "/broker" : "/agency"} replace />
          ) : window.location.pathname.startsWith("/broker") ? (
            user.role === "broker" ? <BrokerPortal me={user} onLogout={logout} /> :
            <Navigate to={user.role === "customer" ? "/portal" : "/agency"} replace />
          ) : (
            user.role === "customer" ? <Navigate to="/portal" replace /> :
            user.role === "broker" ? <Navigate to="/broker" replace /> :
            <StaffWorkspace me={user} onLogout={logout} />
          )
        } />
      </Routes>
    </>
  );
}
